/**
 * CreditWidget — Barra + HoverCard con saldo de créditos IA en el topbar.
 * Extraído de DashboardLayout.jsx (iter.246 · Fase 8 UX Refactor).
 *
 * Props:
 *  - creditInfo: objeto de `/subscription/my/{rid}` con `credits_remaining`,
 *    `credits_total`, `pct_used`, `plan_name`, `days_remaining`.
 *  - creditBreakdown: objeto de `/subscription/credits/{rid}/usage-stats`
 *    con `total_credits` y `by_action[]`.
 */
import { Link } from "react-router-dom";
import { Coins } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../ui/hover-card";

export function CreditWidget({ creditInfo, creditBreakdown }) {
  if (!creditInfo) return null;

  const pct = creditInfo.pct_used || 0;
  const remaining = creditInfo.credits_remaining || 0;
  const total = creditInfo.credits_total || 0;
  const isCritical = pct >= 90 || remaining < 10;
  const isLow = pct >= 70 || (total > 0 && remaining / total < 0.2);
  const barCls = isCritical ? "bg-red-500" : isLow ? "bg-amber-500" : "bg-emerald-500";
  const topAction = creditBreakdown?.by_action?.[0];
  const weeklyTotal = creditBreakdown?.total_credits || 0;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <Link
          to="/app/subscription"
          className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
            isCritical
              ? "bg-red-500/10 hover:bg-red-500/15 ring-1 ring-red-500/40 animate-pulse"
              : "bg-muted/60 hover:bg-muted"
          }`}
          data-testid="credit-widget"
        >
          <Coins className={`w-3.5 h-3.5 ${isCritical ? "text-red-500" : "text-primary"}`} />
          <div className="flex items-center gap-1.5">
            <div className="w-16 h-1.5 bg-muted-foreground/20 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barCls}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
            <span
              className={`text-[10px] font-medium ${
                isCritical ? "text-red-700 font-bold" : "text-muted-foreground"
              }`}
            >
              {remaining}
            </span>
          </div>
        </Link>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        className="w-72 p-3"
        data-testid="credit-widget-hovercard"
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Créditos IA · {creditInfo.plan_name}
          </p>
          {isCritical && (
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/15 text-red-700">
              Crítico
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1.5 mb-1">
          <span className="text-2xl font-bold font-heading">
            {remaining.toLocaleString()}
          </span>
          <span className="text-xs text-muted-foreground">
            / {total.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-1">
          <div
            className={`h-full rounded-full ${barCls}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
        <p className="text-[10px] text-muted-foreground mb-3">
          {pct.toFixed(1)}% usado · renueva en {creditInfo.days_remaining ?? 0} días
        </p>

        <div className="border-t pt-2">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Últimos 7 días
          </p>
          {weeklyTotal === 0 ? (
            <p className="text-xs text-muted-foreground">Sin consumo de IA.</p>
          ) : (
            <>
              <p className="text-xs mb-1">
                Has consumido <span className="font-semibold">{weeklyTotal}</span> créditos
              </p>
              {topAction && (
                <p className="text-[11px] text-muted-foreground">
                  Principal uso:{" "}
                  <span className="font-medium text-foreground">
                    {topAction.action.replace(/_/g, " ")}
                  </span>{" "}
                  ({topAction.credits} créd.)
                </p>
              )}
            </>
          )}
        </div>

        {(isLow || isCritical) && (
          <div
            className={`mt-2 text-[11px] rounded-md p-2 ${
              isCritical ? "bg-red-500/10 text-red-700" : "bg-amber-500/10 text-amber-700"
            }`}
          >
            Considera una recarga para evitar interrupciones.
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
